import { type ReactElement } from 'react';
import styles from './index-card.module.scss';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { Font } from '@utmb/design-system/text/font';
import { getCategoryColor, getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { Icon } from '@utmb/design-system/icons/Icon';
import { IconsEnum } from '@utmb/types/icons';
import isMobile from '@utmb/utils/is-mobile';
import { VALID_INDEX_THRESHOLD } from '@utmb/utils/utmb-index.constants';
import { useTranslation } from 'next-i18next';

export interface IndexCardProps {
    category: PiCategoryEnum;
    value?: number;
    ranking?: number | null;
    isFrozen?: boolean;
    onDetailsClick?: () => void;
}

export function IndexCard({ category, value, ranking, isFrozen = false, onDetailsClick }: IndexCardProps): ReactElement {
    const { t } = useTranslation();
    const isClickable = !isFrozen && !!value && !!onDetailsClick;
    const className =
        styles.indexCard + (!value ? ` ${styles.lockedIndexCard}` : '') + (isFrozen ? ` ${styles.frozen}` : '') + (isClickable ? ` ${styles.clickable}` : '');
    const Wrapper = isClickable ? 'button' : 'div';
    const wrapperProps = isClickable ? { type: 'button' as const, onClick: onDetailsClick } : {};
    const categoryColor = getCategoryColor(category).replace(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i, '#$1$1$2$2$3$3');
    const frozenIconColor = category === PiCategoryEnum.PI20K ? 'var(--utmb-color-vc)' : categoryColor;
    return (
        <Wrapper className={className} {...wrapperProps}>
            <div className={styles.wrapColor} style={{ backgroundColor: categoryColor + '1A', borderColor: categoryColor + '4D' }}>
                <div className={styles.category}>
                    <PiCategoryLogo height={27} piCategory={category} isDark={false} />
                </div>
                {value ? (
                    isFrozen ? (
                        <div className={styles.frozenContent}>
                            <div className={styles.frozenScoreRow}>
                                <Icon
                                    clss={styles.frozenLogo}
                                    variant={IconsEnum.FROZEN_INDEX}
                                    color={frozenIconColor}
                                    width={isMobile() ? 27 : 37}
                                    height={isMobile() ? 27 : 37}
                                />
                                <Font.OxaniumMedium
                                    as="span"
                                    className={styles.value}
                                    desktop="24"
                                    mobile="16"
                                    color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                >
                                    {value}
                                </Font.OxaniumMedium>
                            </div>
                            {ranking ? (
                                <Font.FuturaBook as="span" desktop="16" mobile="14">
                                    {t('singleRunner.statistics.distribution.rank', { count: ranking, ordinal: true })}
                                </Font.FuturaBook>
                            ) : null}
                        </div>
                    ) : (
                        <div className={styles.valueRow}>
                            {value > VALID_INDEX_THRESHOLD ? (
                                <Font.OxaniumMedium
                                    as="span"
                                    className={styles.value}
                                    desktop="64"
                                    mobile="18"
                                    color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                >
                                    {value}
                                </Font.OxaniumMedium>
                            ) : (
                                <Font.OxaniumMedium
                                    as="span"
                                    className={styles.value + ' ' + styles.validValue}
                                    desktop="20"
                                    mobile="14"
                                    color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                >
                                    {t('singleRunner.statistics.global.validIndex')}
                                </Font.OxaniumMedium>
                            )}
                        </div>
                    )
                ) : null}
            </div>
            {!value ? (
                <div className={styles.lock}>
                    <Icon variant={IconsEnum.LOCK} color={getColorStyle(ColorTypesEnum.BLUE_LIGHT)} />
                </div>
            ) : null}

            {(!isFrozen || !value) && (
                <div className={styles.footer}>
                    {value ? (
                        <>
                            <Font.FuturaMedium as="span" desktop="16" mobile="16">
                                {t('races.showMore')}
                            </Font.FuturaMedium>
                            <Icon variant={IconsEnum.CHEVRON_DOWN} />
                        </>
                    ) : (
                        <Font.FuturaMedium className={styles.noIndex} as="span" desktop="16" mobile="16">
                            {t('singleRunner.statistics.indexNoValid')}
                        </Font.FuturaMedium>
                    )}
                </div>
            )}
        </Wrapper>
    );
}
